import {
  GoogleAuthProvider,
  signInWithCredential,
  type UserCredential,
} from 'firebase/auth'
import { auth } from '@/config/firebase'

// Google OAuth client id (public) — same client the agent-voz desktop uses;
// http://localhost:8659/callback is already an authorized redirect URI on it.
const GOOGLE_CLIENT_ID =
  process.env.VUE_APP_GOOGLE_OAUTH_CLIENT_ID ||
  '1057261681378-mc7e2vhqc0fj7tdskhaa93k1b34omq4h.apps.googleusercontent.com'

// Port 8660 (not the agent-voz desktop's 8659) so both apps can coexist.
const OAUTH_REDIRECT_URI = 'http://localhost:8660/callback'

/** True when running inside the Tauri desktop shell (vs. the web browser). */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
}

/**
 * Google sign-in for the Tauri desktop shell. Google blocks OAuth inside
 * embedded webviews, so instead of signInWithPopup we open the system browser
 * (implicit flow), capture the access token via a loopback server
 * (src-tauri/oauth_server.rs emits the "oauth-token" event), and exchange it for
 * a Firebase credential. Mirrors the agent-voz desktop flow. Same Firebase
 * project (torcazastudio) → same user/session semantics as web.
 */
export async function signInWithGoogleDesktop(): Promise<UserCredential> {
  const { open } = await import('@tauri-apps/plugin-shell')
  const { listen } = await import('@tauri-apps/api/event')

  const authUrl =
    'https://accounts.google.com/o/oauth2/v2/auth' +
    `?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}` +
    '&response_type=token' +
    `&redirect_uri=${encodeURIComponent(OAUTH_REDIRECT_URI)}` +
    `&scope=${encodeURIComponent('openid email profile')}` +
    '&prompt=select_account'

  // Register the loopback listener BEFORE opening the browser so we never miss
  // the token event, then open Google in the system browser.
  let unlisten: (() => void) | undefined
  const accessToken = await new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error('Tiempo de espera agotado para el login de Google')),
      180_000,
    )
    listen<string>('oauth-token', (event) => {
      clearTimeout(timeout)
      resolve(event.payload)
    })
      .then((fn) => {
        unlisten = fn
        open(authUrl).catch((e) => {
          clearTimeout(timeout)
          reject(e)
        })
      })
      .catch((e) => {
        clearTimeout(timeout)
        reject(e)
      })
  }).finally(() => unlisten?.())

  const credential = GoogleAuthProvider.credential(null, accessToken)
  return signInWithCredential(auth, credential)
}
